import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { ConfluenceFolderAri } from '@atlassian/ari/confluence';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useSyncSmartFolderMutation$data,
	useSyncSmartFolderMutation$variables,
} from './__generated__/useSyncSmartFolderMutation.graphql.ts';

type SyncSmartFolderInput = {
	folderId: string;
};

type UseSyncSmartFolderResult = {
	syncSmartFolder: (
		input: SyncSmartFolderInput,
	) => Promise<{ data?: useSyncSmartFolderMutation$data }>;
	called: boolean;
	data: useSyncSmartFolderMutation$data | undefined;
	error: Error | undefined;
	loading: boolean;
};

export const useSyncSmartFolder = (): UseSyncSmartFolderResult => {
	const { activationId, cloudId } = useSessionData();

	const [syncSmartFolderMutation, { called, data, error, loading }] = useMutationInterop<
		useSyncSmartFolderMutation$data,
		useSyncSmartFolderMutation$variables
	>(graphql`
		mutation useSyncSmartFolderMutation($cloudId: ID!, $input: ConfluenceSyncSmartFolderInput!) {
			confluence_syncSmartFolder(cloudId: $cloudId, input: $input) {
				errors {
					message
				}
				success
			}
		}
	`);

	const syncSmartFolder = useCallback(
		({ folderId }: SyncSmartFolderInput) => {
			const folderAri = ConfluenceFolderAri.create({
				activationId,
				folderId,
				siteId: cloudId,
			}).toString();

			return syncSmartFolderMutation({
				variables: {
					cloudId,
					input: {
						folderId: folderAri,
					},
				},
			});
		},
		[activationId, cloudId, syncSmartFolderMutation],
	);

	return {
		syncSmartFolder,
		called,
		data,
		error,
		loading,
	};
};
